// Class energy blades (melee lane). The first R3 press stows the gun and draws the class's blade (the draw is a
// strike), more presses chain three flourishes. The sim only knows "blade out" and the flourish step; everything
// here is the look: the hilt that rides the hip, ignition, the blade itself (white-hot core in a coloured glow),
// a swept trail, and the arms, which are posed entirely by IK from authored blade paths.
//
// Why paths and not clips: the swing IS the blade's path through space, so each flourish is authored as keys of
// (hand position, blade direction, chest twist / lean / spin) in the cowboy's aim frame and interpolated with
// Hermite curves. The arms follow by two-bone IK; the trail is the same curve sampled backwards in time, so it is
// a clean arc at any frame rate. No re-bake, no clip library, and it can't fight the gun animations.
//
//   Gunslinger  BRAND       a one-handed brass-hot sabre with a revolver grip; cross-draw from the hip, quick cuts,
//                           and a lunge that ends in a revolver twirl. Pistol-whip primer: every cut primes.
//   Valkyrie    PYRE GLAIVE a telescoping two-handed glaive with a flame blade; wide sweeps, an overhead cleave
//                           and a full spin that leaves a ring of fire. Her primer is the fire dash.
//   Phantom     WHISPERS    twin reverse-grip daggers, ice-cyan, nearly invisible in the cloak; an X draw, a slash,
//                           a double stab and a spin into a two-handed stab. Backstab primer.
//   Juggernaut  BREAKER     an energy-headed maul that rides the shoulder; a ram (the shield-bash primer), two
//                           heavy swings and an overhead slam that throws a shockwave ring.
//
// Per frame it allocates nothing. Geometry and the hilt material are shared per class; the energy materials are
// per cowboy (intensity follows ignition and the cloak).
import * as THREE from 'three';
import type { CowboyRS } from '../contracts';
import type { CowboyAsset } from './asset';
import { printMaterial, paint } from '../materials';
import { PAL } from '../palette';

// ------------------------------------------------------------------ authoring
const D2R = Math.PI / 180;
/** pose layout: right hand (azimuth around the spine, height, reach), blade yaw/pitch; left hand the same;
 *  chest twist, forward lean, whole-body spin, the lunge (0..1: front foot steps in, body carried forward) and the
 *  crouch (m the hips drop). Angles in degrees when authored; + is to the cowboy's right / up. */
const N = 15;
const AZ = 0, H = 1, DD = 2, YAW = 3, PIT = 4, LAZ = 5, LH = 6, LD = 7, LYAW = 8, LPIT = 9, TW = 10, LN = 11, SP = 12, FW = 13, CR = 14;
const ANG = [AZ, YAW, PIT, LAZ, LYAW, LPIT, TW, LN, SP];
const IS_ANG = new Uint8Array(N); for (const j of ANG) IS_ANG[j] = 1;
type Hand = [az: number, h: number, d: number, yaw?: number, pitch?: number];
interface KeyIn { t: number; r?: Hand; l?: Hand; tw?: number; ln?: number; sp?: number; fw?: number; cr?: number }
interface Track { t: number[]; p: Float32Array; dur: number; /** the first key is authored (the draw from the hip) */ fixedStart: boolean; slamAt?: number; /** contact: the key that steps in hardest */ hit: number }

function pose(k: KeyIn, prev: Float32Array | null): Float32Array {
  const o = prev ? prev.slice() : new Float32Array(N);
  if (!prev) o[TW] = o[LN] = o[SP] = o[FW] = o[CR] = 0;
  const put = (h: Hand | undefined, i0: number) => {
    if (!h) return;
    o[i0] = h[0] * D2R; o[i0 + 1] = h[1]; o[i0 + 2] = h[2];
    if (h[3] !== undefined) o[i0 + 3] = h[3] * D2R;
    if (h[4] !== undefined) o[i0 + 4] = h[4] * D2R;
  };
  put(k.r, AZ); put(k.l, LAZ);
  if (k.tw !== undefined) o[TW] = k.tw * D2R;
  if (k.ln !== undefined) o[LN] = k.ln * D2R;
  if (k.fw !== undefined) o[FW] = k.fw;
  if (k.cr !== undefined) o[CR] = k.cr;
  if (k.sp !== undefined) o[SP] = k.sp * D2R;
  else if (prev) o[SP] = prev[SP];
  return o;
}
/** keys carry forward anything they don't set; tw / ln / fw / cr ease back to 0 unless a key says otherwise */
function track(keys: KeyIn[], guard: Float32Array, fixedStart: boolean, slamAt?: number): Track {
  const t: number[] = [];
  const p = new Float32Array(keys.length * N);
  let prev: Float32Array = guard;
  let hit = 0, best = -1;
  keys.forEach((k, i) => {
    const kk = { ...k, tw: k.tw ?? 0, ln: k.ln ?? 0, fw: k.fw ?? 0, cr: k.cr ?? 0 };
    if (kk.fw > best + 1e-6) { best = kk.fw; hit = k.t; }
    prev = pose(kk, prev);
    p.set(prev, i * N);
    t.push(k.t);
  });
  return { t, p, dur: t[t.length - 1], fixedStart, slamAt: slamAt, hit: slamAt ?? hit };
}

interface ClassBlade {
  name: string;
  color: number;
  /** blade length scale on the hilt frame, where the energy starts / ends (m along the blade axis) */
  e0: number; e1: number;
  /** the trail's band along the blade axis */
  tr0: number; tr1: number;
  twoHand: number; // >0: the left hand rides the shaft this far back down the blade axis
  dual: boolean;   // a second blade in the left hand
  slab: boolean;   // the energy is a head on the shaft (the maul): it swells in place instead of shooting out
  reverse: boolean; // reverse grip: the blade leaves the little-finger side of the fist
  guard: Float32Array;
  run: Float32Array;
  steps: Track[];
}

function cls(def: { name: string; color: number; e0: number; e1: number; tr: [number, number]; twoHand?: number; dual?: boolean; reverse?: boolean; guard: KeyIn; run: KeyIn; steps: { keys: KeyIn[]; slam?: number }[] }): ClassBlade {
  const guard = pose(def.guard, null);
  const run = pose(def.run, guard);
  return {
    name: def.name, color: def.color, e0: def.e0, e1: def.e1, tr0: def.tr[0], tr1: def.tr[1], twoHand: def.twoHand ?? 0, dual: !!def.dual, slab: def.name === 'BREAKER', reverse: !!def.reverse,
    guard, run, steps: def.steps.map((s, i) => track(s.keys, guard, i === 0, s.slam)),
  };
}

// Heights are for a 1.9 m cowboy with the shoulder joint at 1.5 m; reach is in metres from the spine axis.
// Strikes cross the front ~0.1 s in (the sim lands the hit on the press), each flourish ends wound up for the next.
const HIP_R: Hand = [-42, 0.98, 0.22, -150, -55]; // cross-draw: the hilt rides the left hip
export const BLADES: ClassBlade[] = [
  cls({
    name: 'BRAND', color: 0xffbe48, e0: 0.13, e1: 0.98, tr: [0.3, 1.03],
    guard: { t: 0, r: [28, 1.12, 0.38, 12, 48], l: [-38, 1.0, 0.28], tw: -29 },
    run: { t: 0, r: [62, 1.02, 0.3, 165, -25], l: [-40, 1.08, 0.34], tw: 0 },
    steps: [
      { keys: [ // the quick-draw: up out of the hip across the body
        { t: 0, r: HIP_R, l: [-70, 1.0, 0.24], tw: -48 },
        { t: 0.05, r: [-25, 1.08, 0.4, -60, -35], tw: -40 },
        { t: 0.1, r: [10, 1.3, 0.5, 20, 20], l: [-45, 1.05, 0.3], fw: 0.8, cr: 0.09 },
        { t: 0.17, r: [50, 1.55, 0.45, 75, 60], tw: 40, fw: 0.5, cr: 0.06 },
        { t: 0.29, r: [40, 1.45, 0.52, 70, -110], tw: 34 }, // the wrist rolls it over, a full turn
        { t: 0.42, r: [45, 1.58, 0.33, 55, -272], tw: 29 },
      ] },
      { keys: [ // diagonal cut down to the left
        { t: 0.04, r: [35, 1.64, 0.42, 40, 72], tw: 32 },
        { t: 0.1, r: [0, 1.3, 0.52, -5, 0], tw: -8, ln: 8, fw: 0.8, cr: 0.09 },
        { t: 0.17, r: [-45, 0.95, 0.45, -70, -45], l: [-20, 1.05, 0.3], tw: -56, ln: 10, fw: 0.5, cr: 0.06 },
        { t: 0.3, r: [-30, 1.35, 0.52, -85, -195], tw: -52, ln: 5 }, // a backward wrist spin
        { t: 0.42, r: [-40, 1.05, 0.4, -95, -370], tw: -48 },
      ] },
      { keys: [ // flat backhand back across
        { t: 0.04, r: [-30, 1.25, 0.45, -75, 5], tw: -48 },
        { t: 0.1, r: [10, 1.32, 0.52, 15, 5], l: [-50, 1.05, 0.3], fw: 0.8, cr: 0.09 },
        { t: 0.17, r: [55, 1.3, 0.45, 100, 10], tw: 56, fw: 0.5, cr: 0.06 },
        { t: 0.29, r: [15, 1.78, 0.22, 170, 70], tw: -6, ln: -12, sp: -28, cr: 0.03 }, // the halo: over the hat behind the head, the body turning under it
        { t: 0.42, r: [40, 1.15, 0.33, 300, 20], tw: 24, sp: 0 },
      ] },
      { keys: [ // lunge, then twirl it like a six-gun on the way back to guard
        { t: 0.05, r: [28, 1.15, 0.25, 5, 5], tw: 32 },
        { t: 0.11, r: [5, 1.3, 0.62, 0, 3], l: [-60, 1.1, 0.35], tw: -40, ln: 14, fw: 0.9, cr: 0.11 },
        { t: 0.22, r: [32, 1.2, 0.45, 10, -60], tw: -8, ln: 5, fw: 0.5, cr: 0.06 },
        { t: 0.3, r: [45, 1.15, 0.4, 10, -180] },
        { t: 0.38, r: [45, 1.15, 0.4, 10, -270] },
        { t: 0.47, r: [32, 1.13, 0.38, 12, -312], l: [-38, 1.0, 0.28], tw: -24 },
      ] },
    ],
  }),
  cls({
    name: 'PYRE GLAIVE', color: 0xff1035, e0: 0.55, e1: 1.38, tr: [0.35, 1.48], twoHand: 0.42,
    guard: { t: 0, r: [22, 1.1, 0.35, -22, 55], tw: -10 },
    run: { t: 0, r: [40, 1.05, 0.3, 168, -18], tw: 10 },
    steps: [
      { keys: [ // the staff telescopes out of the hip and whirls into a sweep
        { t: 0, r: HIP_R, tw: -30 },
        { t: 0.06, r: [-20, 1.2, 0.4, -80, 10], tw: -35 },
        { t: 0.12, r: [10, 1.3, 0.5, 15, 10], tw: -5, fw: 0.6, cr: 0.07 },
        { t: 0.2, r: [50, 1.3, 0.42, 110, 15], tw: 35, fw: 0.36, cr: 0.049 },
        { t: 0.27, r: [30, 1.32, 0.42, 230, 2], tw: 40 }, // the staff whirls flat round her at chest height
        { t: 0.35, r: [30, 1.32, 0.42, 380, 2], tw: 40 },
        { t: 0.44, r: [40, 1.45, 0.35, 510, 40], tw: 40 },
      ] },
      { keys: [ // wide sweep right to left
        { t: 0.05, r: [35, 1.35, 0.45, 80, 10], tw: 35 },
        { t: 0.11, r: [0, 1.3, 0.52, -5, 0], ln: 8, fw: 0.6, cr: 0.07 },
        { t: 0.19, r: [-45, 1.3, 0.45, -110, 5], tw: -40, fw: 0.36, cr: 0.049 },
        { t: 0.27, r: [-30, 1.32, 0.42, -230, -2], tw: -38 }, // and the other way round
        { t: 0.35, r: [-30, 1.32, 0.42, -380, -2], tw: -38 },
        { t: 0.44, r: [-35, 1.5, 0.35, -510, 50], tw: -35 },
      ] },
      { keys: [ // overhead cleave
        { t: 0.05, r: [-10, 1.75, 0.25, -40, 110], tw: -15 },
        { t: 0.11, r: [5, 1.45, 0.5, 0, 15], ln: 15, fw: 0.6, cr: 0.07 },
        { t: 0.18, r: [5, 0.78, 0.55, 0, -62], tw: 5, ln: 32, fw: 0.36, cr: 0.11 },
        { t: 0.25, r: [8, 0.84, 0.54, 5, -56], tw: 8, ln: 28, fw: 0.3, cr: 0.1 }, // it bites the ground and holds
        { t: 0.34, r: [30, 1.08, 0.42, 45, -25], tw: 24, ln: 6, cr: 0.04 }, // ripped back out past the hip
        { t: 0.44, r: [34, 1.24, 0.4, 72, 0], tw: 34 }, // wound for the turn
      ] },
      { keys: [ // the full turn: a ring of fire
        { t: 0.05, r: [30, 1.25, 0.4, 70, 0], tw: 30 },
        { t: 0.1, r: [5, 1.3, 0.5, 0, 0], sp: 0, fw: 0.6, cr: 0.07 },
        { t: 0.22, r: [-30, 1.25, 0.52, -80, 5], tw: -30, sp: -180, fw: 0.36, cr: 0.07 },
        { t: 0.34, r: [0, 1.28, 0.5, 0, 5], sp: -360, cr: 0.05 },
        { t: 0.45, r: [22, 1.1, 0.35, -22, 55], tw: -10, sp: -360 },
      ] },
    ],
  }),
  cls({
    name: 'WHISPERS', color: 0x5fe0ff, e0: 0.075, e1: 0.55, tr: [0.06, 0.58], dual: true, reverse: true,
    guard: { t: 0, r: [30, 1.32, 0.38, 40, -18], l: [-30, 1.36, 0.4, -40, -18], ln: 8 },
    run: { t: 0, r: [52, 1.1, 0.3, 170, -12], l: [-52, 1.1, 0.3, -170, -12], ln: 12 },
    steps: [
      { keys: [ // both out of the hips, crossed in an X
        { t: 0, r: [40, 0.98, 0.24, 160, -60], l: [-40, 0.98, 0.24, -160, -60] },
        { t: 0.05, r: [30, 1.35, 0.4, 60, 20], l: [-30, 1.35, 0.4, -60, 20] },
        { t: 0.1, r: [-20, 1.15, 0.5, -60, -40], l: [20, 1.1, 0.5, 60, -40], ln: 22, fw: 1.0, cr: 0.1 },
        { t: 0.18, r: [-40, 1.0, 0.42, -100, -70], l: [40, 1.0, 0.42, 100, -70], ln: 18, fw: 0.6, cr: 0.07 },
        { t: 0.3, r: [22, 1.52, 0.48, -20, -250], l: [-22, 1.5, 0.46, 20, -250], ln: 6 }, // both flip in the fingers
        { t: 0.42, r: [32, 1.35, 0.36, 60, -360], l: [-26, 1.3, 0.32, -10, -420], ln: 5 },
      ] },
      { keys: [ // the right one opens a slash across
        { t: 0.04, r: [40, 1.35, 0.45, 90, -10], tw: 25 },
        { t: 0.1, r: [0, 1.3, 0.5, 0, -20], tw: -10, ln: 10, fw: 0.75, cr: 0.05 },
        { t: 0.17, r: [-45, 1.25, 0.42, -90, -20], tw: -35, fw: 0.45, cr: 0.035 },
        { t: 0.29, r: [-25, 1.52, 0.48, -55, -200], tw: -30 }, // a flip on the way back
        { t: 0.42, r: [-20, 1.3, 0.35, -40, -420], l: [-10, 1.3, 0.3, 0, -60], tw: -25 },
      ] },
      { keys: [ // left stab, right stab
        { t: 0.04, l: [-15, 1.3, 0.25, 0, -10] },
        { t: 0.09, l: [-5, 1.3, 0.56, 0, 0], tw: -22, ln: 10, fw: 0.75, cr: 0.05 },
        { t: 0.16, l: [-25, 1.3, 0.3, -20, -60], r: [15, 1.3, 0.25, 0, -10], fw: 0.45, cr: 0.035 },
        { t: 0.22, r: [5, 1.3, 0.56, 0, 0], tw: 22, ln: 15, fw: 0.45, cr: 0.035 },
        { t: 0.32, l: [-25, 1.52, 0.46, -20, -240] }, // the left one flips while the right stabs home
        { t: 0.42, r: [26, 1.3, 0.33, 15, -55], l: [-26, 1.34, 0.36, -15, -422], ln: 5 },
      ] },
      { keys: [ // a spin with both blades out, into the stab from behind
        { t: 0.06, r: [60, 1.3, 0.45, 100, -10], l: [-60, 1.3, 0.45, -100, -10], sp: 30 },
        { t: 0.18, sp: 200, fw: 0.45, cr: 0.035 },
        { t: 0.27, r: [20, 1.35, 0.3, 0, 20], l: [-20, 1.35, 0.3, 0, 20], sp: 340 },
        { t: 0.33, r: [8, 1.3, 0.58, 0, -5], l: [-8, 1.3, 0.58, 0, -5], sp: 360, ln: 25, fw: 0.95, cr: 0.08 },
        { t: 0.47, r: [26, 1.3, 0.33, 15, -62], l: [-26, 1.34, 0.36, -15, -62], sp: 360, ln: 8 },
      ] },
    ],
  }),
  cls({
    name: 'BREAKER', color: 0x7a4dff, e0: 0.82, e1: 1.2, tr: [0.7, 1.28], twoHand: 0.34,
    guard: { t: 0, r: [36, 1.2, 0.32, 30, 105], tw: -10 },
    run: { t: 0, r: [36, 1.2, 0.3, 30, 110], tw: -5 },
    steps: [
      { keys: [ // out of the hip into a ram: the shield bash
        { t: 0, r: HIP_R },
        { t: 0.06, r: [-10, 1.2, 0.35, -30, 0], tw: -20 },
        { t: 0.11, r: [0, 1.3, 0.56, 0, 0], ln: 20, fw: 0.5, cr: 0.1 },
        { t: 0.22, r: [10, 1.3, 0.45, 0, 20], ln: 10, fw: 0.3, cr: 0.07 },
        { t: 0.44, r: [35, 1.25, 0.32, 60, 100], tw: 20 },
      ] },
      { keys: [ // heavy swing right to left
        { t: 0.05, r: [45, 1.45, 0.4, 110, 40], tw: 40 },
        { t: 0.12, r: [0, 1.25, 0.5, 0, 8], ln: 10, fw: 0.5, cr: 0.1 },
        { t: 0.21, r: [-50, 1.02, 0.42, -110, -30], tw: -45, ln: 14, fw: 0.3, cr: 0.07 },
        { t: 0.3, r: [-62, 1.1, 0.4, -150, -15], tw: -58, cr: 0.06 }, // the weight carries him round
        { t: 0.44, r: [-35, 1.0, 0.42, -80, -35], tw: -35 },
      ] },
      { keys: [ // rising backhand
        { t: 0.05, r: [-35, 1.0, 0.42, -80, -35], tw: -35 },
        { t: 0.12, r: [5, 1.25, 0.5, 10, 15], fw: 0.5, cr: 0.1 },
        { t: 0.21, r: [40, 1.6, 0.35, 60, 80], tw: 30, fw: 0.3, cr: 0.07 },
        { t: 0.31, r: [32, 1.62, 0.34, 50, 155], tw: 18, ln: -8, cr: 0.06 }, // the head swings on behind the shoulder
        { t: 0.44, r: [10, 1.9, 0.3, 8, 135], tw: 4, ln: -14 }, // up tall, back arched: the load for the slam
      ] },
      { slam: 0.19, keys: [ // overhead slam
        { t: 0.06, r: [5, 1.8, 0.2, 0, 140], ln: -10 },
        { t: 0.13, r: [0, 1.25, 0.5, 0, 0], ln: 15, fw: 0.5, cr: 0.1 },
        { t: 0.19, r: [0, 0.85, 0.5, 0, -60], ln: 35, fw: 0.6, cr: 0.21 },
        { t: 0.24, r: [0, 0.93, 0.5, 0, -52], ln: 31, fw: 0.6, cr: 0.19 }, // the head kicks back off the ground
        { t: 0.34, r: [0, 0.87, 0.48, 0, -58], ln: 33, fw: 0.55, cr: 0.2 }, // and he holds the crouch
        { t: 0.48, r: [0, 0.95, 0.45, 0, -50], ln: 25, fw: 0.3, cr: 0.1 },
      ] },
    ],
  }),
];

// ------------------------------------------------------------------ meshes
/** a tube along +Y (u = 0..1 maps to y0..y1) with an elliptical section: `w` along Z (the edge), `th` along X */
function tube(y0: number, y1: number, w: (u: number) => number, th: (u: number) => number, bend: (u: number) => number, nu = 16, nv = 10) {
  const pos: number[] = [], idx: number[] = [];
  for (let i = 0; i <= nu; i++) {
    const u = i / nu, y = y0 + (y1 - y0) * u, ww = w(u), tt = th(u), bz = bend(u);
    for (let j = 0; j < nv; j++) {
      const a = (j / nv) * Math.PI * 2;
      pos.push(Math.cos(a) * tt, y, Math.sin(a) * ww + bz);
    }
  }
  for (let i = 0; i < nu; i++) for (let j = 0; j < nv; j++) {
    const a = i * nv + j, b = i * nv + ((j + 1) % nv), c = a + nv, d = b + nv;
    idx.push(a, c, b, b, c, d);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}
const cyl = (r0: number, r1: number, y0: number, y1: number, col: number, seg = 10) => {
  const g = new THREE.CylinderGeometry(r1, r0, y1 - y0, seg, 1);
  g.translate(0, (y0 + y1) / 2, 0);
  return paint(g, col);
};
const box = (x: number, y: number, z: number, cx: number, cy: number, cz: number, col: number) => paint(new THREE.BoxGeometry(x, y, z).translate(cx, cy, cz), col);
/** two plain geometries (position + normal) into one */
function mergeGeo(a: THREE.BufferGeometry, b: THREE.BufferGeometry) {
  const x = a.index ? a.toNonIndexed() : a, y = b.index ? b.toNonIndexed() : b;
  const o = new THREE.BufferGeometry();
  const cat = (k: string) => { const p = x.attributes[k].array as Float32Array, q = y.attributes[k].array as Float32Array; const r = new Float32Array(p.length + q.length); r.set(p); r.set(q, p.length); return r; };
  o.setAttribute('position', new THREE.BufferAttribute(cat('position'), 3));
  o.setAttribute('normal', new THREE.BufferAttribute(cat('normal'), 3));
  return o;
}
function merge(parts: THREE.BufferGeometry[]) {
  const ps: number[] = [], ns: number[] = [], cs: number[] = [];
  for (const g0 of parts) {
    const g = g0.index ? g0.toNonIndexed() : g0;
    g.computeVertexNormals();
    ps.push(...(g.attributes.position.array as Float32Array));
    ns.push(...(g.attributes.normal.array as Float32Array));
    cs.push(...(g.attributes.color.array as Float32Array));
  }
  const o = new THREE.BufferGeometry();
  o.setAttribute('position', new THREE.Float32BufferAttribute(ps, 3));
  o.setAttribute('normal', new THREE.Float32BufferAttribute(ns, 3));
  o.setAttribute('color', new THREE.Float32BufferAttribute(cs, 3));
  return o;
}

interface ClassGeo {
  /** solid hilt parts that don't stretch, and the telescoping shaft (scaled along Y by the extension) */
  hilt: THREE.BufferGeometry; shaft: THREE.BufferGeometry | null; head: THREE.BufferGeometry | null;
  core: THREE.BufferGeometry; glow: THREE.BufferGeometry; outer: THREE.BufferGeometry;
}
const geoCache: (ClassGeo | null)[] = [null, null, null, null];
const STEEL = PAL.gunmetal, BRASS = PAL.brass, WALNUT = 0x5a3a24, DARK = 0x231f1e;
function classGeo(k: number): ClassGeo {
  const c = geoCache[k];
  if (c) return c;
  const sm = (u: number) => u * u * (3 - 2 * u);
  let g: ClassGeo;
  if (k === 0) {
    // BRAND: a revolver grip (walnut, brass back strap), a cylinder-drum guard, a stubby emitter; the blade is a
    // slightly curved single-edged sabre that swells off the guard and runs to a point
    const hilt = merge([
      box(0.034, 0.11, 0.05, 0, -0.035, -0.006, WALNUT), box(0.038, 0.12, 0.012, 0, -0.03, -0.032, BRASS),
      cyl(0.03, 0.03, 0.035, 0.085, BRASS, 8), cyl(0.034, 0.034, 0.05, 0.07, DARK, 8), cyl(0.016, 0.02, 0.085, 0.13, STEEL, 8),
      box(0.012, 0.012, 0.05, 0, 0.06, 0.04, STEEL), // the hammer spur
    ]);
    const L = 0.85, wf = (u: number) => 0.042 * (1 - Math.pow(u, 3)) + 0.008, bend = (u: number) => -0.025 * u * u;
    g = {
      hilt, shaft: null, head: null,
      core: tube(0, L, (u) => wf(u) * 0.55, (u) => 0.0055 * (1 - u * 0.6), bend, 18, 8),
      glow: tube(-0.01, L + 0.03, (u) => wf(u) * 1.9 + 0.02, (u) => 0.028 + 0.01 * (1 - u), bend, 18, 10),
      outer: tube(-0.03, L + 0.08, (u) => wf(u) * 2.8 + 0.045, () => 0.07, bend, 14, 10),
    };
  } else if (k === 1) {
    // PYRE GLAIVE: a brass-banded steel staff that telescopes from 0.3 to 1.3 m, an emitter fork at the top and a
    // crescent of flame that sweeps back from it
    const hilt = merge([cyl(0.024, 0.024, -0.1, 0.1, DARK, 8), cyl(0.028, 0.028, 0.08, 0.11, BRASS, 8), cyl(0.028, 0.028, -0.11, -0.08, BRASS, 8)]);
    const shaft = merge([
      cyl(0.017, 0.017, -0.82, 0.5, STEEL, 8), cyl(0.024, 0.024, -0.84, -0.78, BRASS, 8),
      cyl(0.022, 0.028, 0.46, 0.56, BRASS, 8), box(0.03, 0.09, 0.07, 0, 0.53, 0.02, STEEL),
    ]);
    const L = 0.85, wf = (u: number) => 0.1 * Math.sin(Math.PI * Math.pow(u, 0.6)) + 0.01, bend = (u: number) => -0.16 * u * u + 0.06 * u;
    g = {
      hilt, shaft, head: null,
      core: tube(0, L, (u) => wf(u) * 0.45, (u) => 0.006 * (1 - u * 0.7), bend, 20, 8),
      glow: tube(-0.02, L + 0.04, (u) => wf(u) * 1.35 + 0.015, (u) => 0.026 * (1 - 0.5 * u) + 0.006, bend, 20, 10),
      outer: tube(-0.05, L + 0.1, (u) => wf(u) * 2.4 + 0.055, () => 0.07, bend, 16, 10),
    };
  } else if (k === 2) {
    // WHISPERS: a short black hilt with a cyan-lit ring and a needle of a blade
    const hilt = merge([cyl(0.016, 0.018, -0.075, 0.05, DARK, 8), cyl(0.021, 0.021, 0.04, 0.058, STEEL, 8), box(0.01, 0.01, 0.06, 0, 0.05, 0, STEEL)]);
    const L = 0.48, wf = (u: number) => 0.018 * (1 - sm(u)) + 0.002;
    g = {
      hilt, shaft: null, head: null,
      core: tube(0, L, (u) => wf(u) * 0.55, (u) => 0.004 * (1 - u * 0.5), () => 0, 14, 8),
      glow: tube(-0.01, L + 0.02, (u) => wf(u) * 1.9 + 0.014, (u) => 0.02 * (1 - 0.5 * u) + 0.005, () => 0, 14, 8),
      outer: tube(-0.02, L + 0.06, (u) => wf(u) * 3 + 0.035, () => 0.045, () => 0, 12, 8),
    };
  } else {
    // BREAKER: a thick haft that telescopes into a maul; a chamfered steel head with an energy face on both
    // striking ends (the glow comes off the faces, not the block) and plasma seams down its sides
    const hilt = merge([cyl(0.03, 0.03, -0.12, 0.12, DARK, 8), cyl(0.036, 0.036, 0.1, 0.14, STEEL, 8), cyl(0.036, 0.036, -0.14, -0.1, STEEL, 8)]);
    const shaft = merge([cyl(0.03, 0.03, -0.4, 0.9, STEEL, 8), cyl(0.04, 0.036, -0.46, -0.36, BRASS, 8), cyl(0.036, 0.036, 0.3, 0.36, BRASS, 8)]);
    const HY = 1.0;
    const cham = (sx: number, sy: number, sz: number, cy: number, col: number) => {
      const b = new THREE.CylinderGeometry(1, 1, 1, 8, 1); // an octagonal prism along Z reads as a chamfered block
      b.rotateX(Math.PI / 2).rotateZ(Math.PI / 8).scale(sx, sy, sz).translate(0, cy, 0);
      return paint(b, col);
    };
    const head = merge([
      cham(0.13, 0.13, 0.34, HY, STEEL), cham(0.145, 0.145, 0.05, HY, BRASS).translate(0, 0, 0) ,
      cham(0.14, 0.14, 0.04, HY, DARK).translate(0, 0, 0.15), cham(0.14, 0.14, 0.04, HY, DARK).translate(0, 0, -0.15),
      box(0.05, 0.1, 0.05, 0, HY - 0.15, 0, BRASS),
    ]);
    const faces = (r: number, d: number, off: number) => {
      const a = new THREE.CylinderGeometry(r, r * 0.8, d, 16, 1).rotateX(Math.PI / 2).translate(0, HY, off);
      const b = new THREE.CylinderGeometry(r * 0.8, r, d, 16, 1).rotateX(Math.PI / 2).translate(0, HY, -off);
      return mergeGeo(a, b);
    };
    g = {
      hilt, shaft, head,
      core: faces(0.1, 0.03, 0.185),
      glow: (() => { const a = new THREE.SphereGeometry(1, 16, 10).scale(0.18, 0.18, 0.09).translate(0, HY, 0.2); const b = a.clone().translate(0, 0, -0.4); return mergeGeo(a, b); })(),
      outer: (() => { const a = new THREE.SphereGeometry(1, 16, 10).scale(0.3, 0.3, 0.18).translate(0, HY, 0.23); const b = a.clone().translate(0, 0, -0.46); return mergeGeo(a, b); })(),
    };
  }
  geoCache[k] = g;
  return g;
}

// energy: an HDR white-hot core (it blooms), a fresnel glow shell, a soft outer halo
const CORE_VS = /* glsl */ `
varying float vY;
void main() { vY = position.y; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }`;
const CORE_FS = /* glsl */ `
uniform vec3 uColor; uniform float uInt; uniform float uTime; uniform float uMix;
varying float vY;
void main() {
  float f = 0.92 + 0.08 * sin( uTime * 61.0 + vY * 37.0 );
  gl_FragColor = vec4( mix( vec3( 1.0 ), uColor, uMix ) * uInt * f, 1.0 );
}`;
const GLOW_VS = /* glsl */ `
uniform float uTime; uniform float uFlame;
varying vec3 vN; varying vec3 vV; varying float vY;
void main() {
  vec3 p = position;
  // the glaive's flame licks: the shell breathes along its length, faster toward the tip
  float lick = sin( uTime * 23.0 - p.y * 31.0 ) * 0.5 + sin( uTime * 37.0 - p.y * 53.0 + 1.3 ) * 0.5;
  p.xz *= 1.0 + uFlame * ( 0.25 + 0.35 * lick ) * clamp( p.y * 1.5, 0.0, 1.0 );
  vec4 mv = modelViewMatrix * vec4( p, 1.0 );
  vN = normalize( normalMatrix * normal ); vV = -mv.xyz; vY = p.y;
  gl_Position = projectionMatrix * mv;
}`;
const GLOW_FS = /* glsl */ `
uniform vec3 uColor; uniform float uInt; uniform float uPow; uniform float uTime;
varying vec3 vN; varying vec3 vV; varying float vY;
void main() {
  float ndv = abs( dot( normalize( vN ), normalize( vV ) ) );
  float a = pow( ndv, uPow );
  float f = 0.9 + 0.1 * sin( uTime * 47.0 + vY * 21.0 );
  gl_FragColor = vec4( uColor * a * uInt * f, 1.0 );
}`;
const TRAIL_VS = /* glsl */ `
attribute vec2 aT; // x: age 0 (now) .. 1 (oldest) times visibility, y: 0 inner edge .. 1 tip
varying vec2 vT;
void main() { vT = aT; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }`;
const TRAIL_FS = /* glsl */ `
uniform vec3 uColor; uniform float uInt;
varying vec2 vT;
void main() {
  float a = vT.x;
  // a thin bright rim along the tip's arc over a soft colour wash, white-hot only at the head of the sweep
  // the wash trails the rim by a beat (the head of the cut is the rim alone, never a slab)
  float wash = pow( vT.y, 1.6 ) * 0.32 * ( 1.0 - smoothstep( 0.8, 0.98, a ) );
  float rim = smoothstep( 0.84, 0.95, vT.y ) * ( 1.0 - smoothstep( 0.96, 1.0, vT.y ) );
  vec3 c = uColor * ( wash + rim * 2.0 ) + vec3( 1.0 ) * rim * pow( a, 6.0 ) * 0.35;
  gl_FragColor = vec4( c * a * uInt, 1.0 );
}`;
const RING_FS = /* glsl */ `
uniform vec3 uColor; uniform float uA;
varying vec2 vUv;
void main() {
  float r = vUv.y; // RingGeometry: v runs inner (0) to outer (1)
  float wash = smoothstep( 0.5, 0.92, r ) * 0.1;
  float edge = smoothstep( 0.88, 0.955, r ) * ( 1.0 - smoothstep( 0.965, 0.99, r ) );
  vec3 c = uColor * ( wash + edge * 2.6 ) + vec3( 1.0 ) * edge * edge * uA * 0.8;
  gl_FragColor = vec4( c * uA * 1.3, 1.0 );
}`;

function energyMat(vs: string, fs: string, color: number, extra: Record<string, THREE.IUniform> = {}) {
  return new THREE.ShaderMaterial({
    vertexShader: vs, fragmentShader: fs,
    uniforms: { uColor: { value: new THREE.Color(color) }, uInt: { value: 1 }, uTime: { value: 0 }, uPow: { value: 1.5 }, uFlame: { value: 0 }, ...extra },
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });
}
let hiltMat: THREE.Material | null = null;
let flareTex: THREE.Texture | null = null;
/** a soft star: a radial bloom with four thin rays */
function makeFlareTex() {
  const n = 64, data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const u = (x + 0.5) / n * 2 - 1, v = (y + 0.5) / n * 2 - 1, r = Math.hypot(u, v);
    const core = Math.exp(-r * r * 18), halo = Math.exp(-r * r * 4) * 0.45;
    const rays = (Math.exp(-Math.abs(u) * 40) + Math.exp(-Math.abs(v) * 40)) * Math.max(0, 1 - r) * 0.6;
    const a = Math.min(1, core + halo + rays), w = Math.min(1, core * 1.2);
    const i = (y * n + x) * 4;
    data[i] = data[i + 1] = data[i + 2] = 255; data[i + 3] = Math.round(255 * Math.min(1, a + w * 0.3));
  }
  const t = new THREE.DataTexture(data, n, n);
  t.needsUpdate = true;
  return t;
}

const TRAIL_N = 44;
const TRAIL_DT = 0.0058;

/** one blade: hilt (+ telescoping shaft / maul cage), the energy, and its trail */
class Assembly {
  readonly root = new THREE.Group();
  readonly hilt: THREE.Mesh;
  readonly shaft: THREE.Mesh | null;
  readonly head: THREE.Mesh | null;
  readonly energy = new THREE.Group();
  readonly core: THREE.Mesh;
  readonly glow: THREE.Mesh;
  readonly outer: THREE.Mesh;
  readonly trail: THREE.Mesh;
  /** the ignition flash at the emitter */
  readonly flare: THREE.Sprite;
  readonly tPos = new Float32Array(TRAIL_N * 2 * 3);
  readonly tAttr = new Float32Array(TRAIL_N * 2 * 2);
  readonly mCore: THREE.ShaderMaterial; readonly mGlow: THREE.ShaderMaterial; readonly mOuter: THREE.ShaderMaterial; readonly mTrail: THREE.ShaderMaterial;
  /** the cutting edge (world), damped so the wrist turns instead of snapping */
  readonly edge = new THREE.Vector3(0, 0, -1);
  /** per-class trail brightness: the flame class and the maul burn hottest */
  readonly trailGain: number;
  edgeFresh = true;

  constructor(k: number, parent: THREE.Object3D) {
    const g = classGeo(k), b = BLADES[k];
    hiltMat ??= printMaterial({ vertexColors: true, fogMax: 0.35 });
    this.hilt = new THREE.Mesh(g.hilt, hiltMat);
    this.shaft = g.shaft ? new THREE.Mesh(g.shaft, hiltMat) : null;
    this.head = g.head ? new THREE.Mesh(g.head, hiltMat) : null;
    // the glaive keeps its crimson right into the core (white-hot reads pink once it blooms)
    this.mCore = energyMat(CORE_VS, CORE_FS, b.color, { uMix: { value: k === 1 ? 0.62 : 0.22 } });
    this.trailGain = [1.2, 1.8, 1.2, 1.5][k];
    this.mCore.blending = THREE.NormalBlending; this.mCore.transparent = false; this.mCore.depthWrite = true;
    this.mGlow = energyMat(GLOW_VS, GLOW_FS, b.color);
    this.mOuter = energyMat(GLOW_VS, GLOW_FS, b.color);
    this.mOuter.uniforms.uPow.value = 3.0;
    if (k === 1) { this.mGlow.uniforms.uFlame.value = 1; this.mOuter.uniforms.uFlame.value = 1.4; }
    this.core = new THREE.Mesh(g.core, this.mCore);
    this.glow = new THREE.Mesh(g.glow, this.mGlow);
    this.outer = new THREE.Mesh(g.outer, this.mOuter);
    this.glow.renderOrder = 3; this.outer.renderOrder = 3;
    this.energy.add(this.core, this.glow, this.outer);
    flareTex ??= makeFlareTex();
    this.flare = new THREE.Sprite(new THREE.SpriteMaterial({ map: flareTex, color: b.color, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
    this.flare.renderOrder = 4; this.flare.visible = false; this.flare.frustumCulled = false;
    this.root.add(this.flare);
    this.root.add(this.hilt, this.energy);
    if (this.shaft) this.root.add(this.shaft);
    if (this.head) this.root.add(this.head);
    for (const m of [this.hilt, this.shaft, this.head]) if (m) m.castShadow = true;
    this.root.matrixAutoUpdate = false;

    const tg = new THREE.BufferGeometry();
    tg.setAttribute('position', new THREE.BufferAttribute(this.tPos, 3).setUsage(THREE.DynamicDrawUsage));
    tg.setAttribute('aT', new THREE.BufferAttribute(this.tAttr, 2).setUsage(THREE.DynamicDrawUsage));
    const idx: number[] = [];
    for (let i = 0; i < TRAIL_N - 1; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
    tg.setIndex(idx);
    tg.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e5);
    // the maul's ribbon runs bluer than its faces: additive violet lifts to magenta over warm ground
    this.mTrail = energyMat(TRAIL_VS, TRAIL_FS, k === 3 ? 0x5a5cff : b.color);
    this.trail = new THREE.Mesh(tg, this.mTrail);
    this.trail.frustumCulled = false;
    this.trail.renderOrder = 3;
    this.trail.visible = false;
    for (const o of [this.core, this.glow, this.outer]) { o.frustumCulled = false; o.castShadow = false; }
    parent.add(this.root, this.trail);
  }

  /** place the assembly: blade frame (X = D x E, Y = D the blade, Z = E the edge) at `pos`; ext: shaft 0..1, ign: blade 0..1 */
  place(pos: THREE.Vector3, q: THREE.Quaternion, ext: number, ign: number, intensity: number, b: ClassBlade, time: number, thick = 1, flash = 0) {
    this.root.position.copy(pos); this.root.quaternion.copy(q);
    this.root.updateMatrix();
    const e = 0.3 + 0.7 * ext;
    if (this.shaft) this.shaft.scale.set(1, e, 1);
    if (this.head) { this.head.position.y = 0; this.head.scale.set(1, e, 1); }
    const show = ign > 0.01;
    this.energy.visible = show;
    if (show) {
      // ignition: the blade shoots out with a little overshoot; the maul's slab swells instead
      const s = ign < 1 ? 1 - Math.pow(1 - ign, 3) * Math.cos(ign * 4.2) : 1;
      // thick: the blade keeps a readable width on screen as the camera pulls back
      if (b.slab) { const t = Math.sqrt(thick); this.energy.scale.set(s * t, s * e, s * t); this.energy.position.y = 0; }
      else { this.energy.position.y = b.e0 * e; this.energy.scale.set(thick, s, thick); }
      const flare = 1 + 1.4 * Math.max(0, 1 - Math.abs(ign - 0.85) / 0.15) * (ign < 1 ? 1 : 0);
      this.mCore.uniforms.uInt.value = 3.2 * intensity * flare;
      this.mGlow.uniforms.uInt.value = 1.5 * intensity * flare;
      this.mOuter.uniforms.uInt.value = 0.8 * intensity * flare;
      this.mCore.uniforms.uTime.value = this.mGlow.uniforms.uTime.value = this.mOuter.uniforms.uTime.value = time;
    }
    // the ignition flash: a burst at the emitter as the blade lights
    const fl = Math.max(flash, show && ign < 1 ? Math.sin(Math.PI * Math.min(1, ign * 1.25)) : 0) * intensity;
    this.flare.visible = fl > 0.02;
    if (this.flare.visible) {
      const r = (b.slab ? 0.9 : 0.55) * (0.6 + 0.6 * fl) * Math.sqrt(thick);
      this.flare.position.set(0, b.slab ? 1.0 : b.e0 * e, 0);
      this.flare.scale.set(r, r, r);
      this.flare.material.opacity = Math.min(1, fl * 1.4);
    }
    this.root.updateMatrixWorld(true);
  }

  dispose() {
    this.root.removeFromParent(); this.trail.removeFromParent();
    this.trail.geometry.dispose();
    this.mCore.dispose(); this.mGlow.dispose(); this.mOuter.dispose(); this.mTrail.dispose(); this.flare.material.dispose();
  }
}

// ------------------------------------------------------------------ the per-cowboy driver
/** What the blade needs from its cowboy (client/render/cowboys/index.ts Cowboy). */
/** Full-body mocap under each flourish (bake.ts `mb_*`, sliced so the clip's own strike sits `peak` s in): the hips,
 *  legs and spine play the clip while the arms keep the authored blade path, so a cut gets a real step-in, hip
 *  drive and follow-through (critics, 2026-10-24: "the legs and hips don't take part"). The clip's strike lands on
 *  the blade's (`hit` s into the step) and it runs `sp` times faster than shot. null: the authored body alone (the
 *  spins turn the whole cowboy, and a mocap spin would turn him twice) */
type Body = { clip: string; peak: number; hit: number; sp: number } | null;
const bd = (clip: string, peak = 0.5, hit = 0.1, sp = 1.25): Body => ({ clip: 'mb_' + clip, peak, hit, sp });
export const MOCAP_BODY: Body[][] = [
  [bd('gs_slash4', 0.3), bd('gs_slash'), bd('ax_backhand'), null], // gs_slash3 folds him double and the hat slides off; the lunge is ours
  [bd('ax_combo_v3'), bd('ax_horizontal'), bd('ax_downward'), null],
  [bd('k03'), bd('k01'), bd('k02'), null],
  [bd('gs_attack', 0.47), bd('ax_horizontal'), bd('ax_backhand'), null], // the axe pack's downward cut hops; the slam is ours
];

export interface BladeHost {
  bones: THREE.Bone[];
  group: THREE.Object3D;
  runAmt: number;
  runPhase: number;
  sprintW: number;
  /** 0..1, how far the gun is put away (index.ts holster); the blade lights once it's mostly gone */
  gunStowed: number;
  /** post-rotate bone i about a model-space axis (Cowboy.rot) */
  rotBone(i: number, axis: THREE.Vector3, ang: number): void;
}

const X_AXIS = new THREE.Vector3(1, 0, 0), Y_AXIS = new THREE.Vector3(0, 1, 0);
const UP = new THREE.Vector3(0, 1, 0);
const _v = new THREE.Vector3(), _v2 = new THREE.Vector3(), _v3 = new THREE.Vector3(), _sh = new THREE.Vector3(), _pole = new THREE.Vector3();
const _hilt = new THREE.Vector3(), _D = new THREE.Vector3(), _E = new THREE.Vector3(), _X = new THREE.Vector3();
const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _qb = new THREE.Quaternion();
const _m = new THREE.Matrix4(), _m2 = new THREE.Matrix4();
const _fwd = new THREE.Vector3(), _rgt = new THREE.Vector3();
const _p0 = new Float32Array(N), _p1 = new Float32Array(N), _p2 = new Float32Array(N);
const _cur = new THREE.Vector3(), _off = new THREE.Vector3();
const _footP = [new THREE.Vector3(), new THREE.Vector3()], _kneeP = [new THREE.Vector3(), new THREE.Vector3()], _footQ = [new THREE.Quaternion(), new THREE.Quaternion()];
const BODY_LEAD = 0.035;
const BODY = [TW, LN, FW, CR];
/** hit-stop: at contact the swing clock nearly stops for a beat, then catches up by the end of the flourish */
const STOP_HOLD = 0.055, STOP_HOLD_SLAM = 0.085, STOP_RATE = 0.15;
/** the follow-through spring on the twist and lean (Hz, damping ratio): the body overshoots each cut and settles */
const FT_HZ = 6.5, FT_ZETA = 0.38;
const smooth = (x: number) => x * x * (3 - 2 * x);
const clamp = (x: number, a: number, b: number) => (x < a ? a : x > b ? b : x);
const TAU = Math.PI * 2;
/** shift angle `a` by whole turns to sit within half a turn of `ref` */
const near = (a: number, ref: number) => a + TAU * Math.round((ref - a) / TAU);

/** blade direction from aim-frame yaw / pitch (radians) */
function dirOf(yaw: number, pitch: number, fwd: THREE.Vector3, rgt: THREE.Vector3, out: THREE.Vector3) {
  const cp = Math.cos(pitch);
  return out.copy(fwd).multiplyScalar(Math.cos(yaw) * cp).addScaledVector(rgt, Math.sin(yaw) * cp).addScaledVector(UP, Math.sin(pitch));
}

export class Blade {
  readonly def: ClassBlade;
  private readonly asm: Assembly[] = [];
  private readonly ring: THREE.Mesh | null = null;
  private ringAge = 9;
  private readonly cur = new Float32Array(N);
  private readonly from = new Float32Array(N);
  /** the pose's velocity (per second), so a new flourish carries the last one's momentum instead of starting from rest */
  private readonly vel = new Float32Array(N);
  private readonly prevCur = new Float32Array(N);
  private readonly v0 = new Float32Array(N);
  private hasV0 = false;
  private readonly kp: Float32Array;
  private readonly kt: number[] = [];
  private nk = 0;
  private step = -1;
  private swingT = 9;
  private prevAge = 99;
  private lastStep = -1;
  private drawnW = 0; // arms IK weight
  private ign = 0;
  private wasDrawn = false;
  private slammed = false;
  private readonly ft = new Float32Array(4); // follow-through spring: twist, twist velocity, lean, lean velocity
  private ftInit = false;
  // rig numbers
  private readonly B: Record<string, number>;
  private readonly kH: number; private readonly kA: number;
  private readonly shoulderOff: number; // RightArm height above Spine2 at bind
  private readonly armL: number; private readonly armLL: number;
  private readonly hipLocal: THREE.Matrix4[];
  /** Hips bone local units per metre (the hips position is in the skeleton's own space) */
  private readonly hipUnit: number;

  constructor(readonly cls: number, private readonly a: CowboyAsset, parent: THREE.Object3D) {
    this.def = BLADES[cls & 3];
    for (let i = 0; i < (this.def.dual ? 2 : 1); i++) this.asm.push(new Assembly(cls & 3, parent));
    this.kp = new Float32Array(10 * N);
    this.cur.set(this.def.guard);
    const B: Record<string, number> = {};
    for (const k of ['Hips', 'Spine', 'Spine1', 'Spine2', 'RightArm', 'RightForeArm', 'RightHand', 'LeftArm', 'LeftForeArm', 'LeftHand', 'LeftHandMiddle1', 'RightHandMiddle1', 'LeftUpLeg', 'LeftLeg', 'RightUpLeg', 'RightLeg', 'LeftFoot', 'RightFoot']) B[k] = a.idx[k] ?? -1;
    this.B = B;
    const w = a.bindW;
    this.kH = (w[B.RightArm].y) / 1.5;
    this.shoulderOff = w[B.RightArm].y - w[B.Spine2].y;
    const len = (s: string, e: string) => w[B[s]].distanceTo(w[B[e]]);
    this.armL = len('RightArm', 'RightForeArm') + len('RightForeArm', 'RightHand');
    this.armLL = len('LeftArm', 'LeftForeArm') + len('LeftForeArm', 'LeftHand');
    this.kA = this.armL / 0.585;
    this.hipUnit = Math.abs(a.bones[B.Hips].pos[1]) / Math.max(1e-3, w[B.Hips].y);
    // the hilt rides the hip: the left hip for the first blade, the right for the Phantom's second; model space
    // (character's left is +X, forward +Z), converted to Hips-bone local so it moves with the walk
    const hips = B.Hips, k = a.height / 1.9;
    this.hipLocal = [1, -1].map((side) => {
      const p = new THREE.Vector3(0.175 * k * side, w[hips].y - 0.1 * k, 0.05 * k);
      const d = new THREE.Vector3(0.15 * side, -0.82, 0.55).normalize(); // hilt (blade axis) down and forward
      const e = new THREE.Vector3(0, 0.55, 0.82).normalize();
      e.addScaledVector(d, -e.dot(d)).normalize();
      const x = new THREE.Vector3().crossVectors(d, e);
      const q = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, d, e));
      const model = new THREE.Matrix4().compose(p, q, new THREE.Vector3(1, 1, 1));
      const bind = new THREE.Matrix4().compose(w[hips], a.bindWq[hips], new THREE.Vector3(1, 1, 1));
      return bind.invert().multiply(model);
    });
    if ((cls & 3) === 3) {
      const rg = new THREE.RingGeometry(0.2, 1, 48, 1).rotateX(-Math.PI / 2);
      const rm = new THREE.ShaderMaterial({
        vertexShader: 'varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 ); }',
        fragmentShader: RING_FS, uniforms: { uColor: { value: new THREE.Color(this.def.color) }, uA: { value: 0 } },
        transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
      });
      this.ring = new THREE.Mesh(rg, rm);
      this.ring.visible = false; this.ring.renderOrder = 3; this.ring.frustumCulled = false;
      parent.add(this.ring);
    }
  }

  dispose() {
    for (const s of this.asm) s.dispose();
    if (this.ring) { this.ring.removeFromParent(); this.ring.geometry.dispose(); (this.ring.material as THREE.Material).dispose(); }
  }

  /** how much of the body the flourish's mocap has this frame (set by bodyAt; the authored step-in and crouch give
   *  way to it) */
  bodyW = 0;
  /** the flourish's body clip and where in it this frame is, for index.ts to layer under the arms (stateless: every
   *  viewer derives it from the melee age and step) */
  bodyAt(c: CowboyRS, runAmt: number, out: { clip: string; t: number; w: number }) {
    const b = c.blade ? MOCAP_BODY[this.cls & 3][clamp(c.bladeStep ?? 0, 0, 3)] : null;
    const age = c.meleeAge;
    // in fast, out over the recovery; on the run the legs are running, so it fades with speed
    const w = b ? smooth(clamp(age / 0.08, 0, 1)) * (1 - smooth(clamp((age - 0.45) / 0.35, 0, 1))) * (1 - smooth(clamp(runAmt / 0.5, 0, 1))) : 0;
    this.bodyW = w;
    if (!b || w < 0.001) return null;
    out.clip = b.clip; out.t = Math.max(0, b.peak + (age - b.hit) * b.sp); out.w = w;
    return out;
  }

  /** is the blade (or the tail of its put-away) owning the arms? Read by index.ts to skip the melee clip */
  get active() { return this.drawnW > 0.001; }

  /** the melee age with the hit-stop folded in: monotonic, and back in step with the age by the last key */
  private stopClock(age: number) {
    const tr = this.def.steps[this.step];
    const hit = tr.hit, hold = tr.slamAt !== undefined ? STOP_HOLD_SLAM : STOP_HOLD, end = this.kt[this.nk - 1];
    if (age <= hit || end <= hit + hold + 0.02) return age;
    if (age < hit + hold) return hit + (age - hit) * STOP_RATE;
    if (age >= end) return age;
    const a = hit + hold * STOP_RATE;
    return a + (end - a) * (age - hit - hold) / (end - hit - hold);
  }

  /** Hermite through the step's keys */
  private evalAt(t: number, out: Float32Array) {
    const kt = this.kt, kp = this.kp, n = this.nk;
    if (t <= kt[0]) { for (let j = 0; j < N; j++) out[j] = kp[j]; return; }
    if (t >= kt[n - 1]) { for (let j = 0; j < N; j++) out[j] = kp[(n - 1) * N + j]; return; }
    let i = 0;
    while (i < n - 2 && t >= kt[i + 1]) i++;
    const t0 = kt[i], t1 = kt[i + 1], dt = t1 - t0, u = (t - t0) / dt;
    const u2 = u * u, u3 = u2 * u;
    const h00 = 2 * u3 - 3 * u2 + 1, h10 = u3 - 2 * u2 + u, h01 = -2 * u3 + 3 * u2, h11 = u3 - u2;
    for (let j = 0; j < N; j++) {
      const p0 = kp[i * N + j], p1 = kp[(i + 1) * N + j];
      const m0 = i > 0 ? (p1 - kp[(i - 1) * N + j]) / (t1 - kt[i - 1]) : this.hasV0 ? this.v0[j] : 0;
      const m1 = i + 2 < n ? (kp[(i + 2) * N + j] - p0) / (kt[i + 2] - t0) : 0;
      out[j] = h00 * p0 + h10 * dt * m0 + h01 * p1 + h11 * dt * m1;
    }
  }

  private begin(step: number, cold: boolean) {
    const tr = this.def.steps[step];
    this.step = step; this.swingT = 0; this.slammed = false; this.hasV0 = false;
    let n = 0;
    if (!tr.fixedStart || !cold) {
      // start from wherever the blade is now, by whole turns the nearest way round to the first key
      this.from.set(this.cur);
      for (const j of ANG) this.from[j] = near(this.from[j], tr.p[j]);
      this.kp.set(this.from, 0); this.kt[0] = 0; n = 1;
      // flow: the chain picks the blade up mid-motion (follow-through runs on into the next wind-up)
      for (let j = 0; j < N; j++) { const lim = IS_ANG[j] ? 9 : 2.5; this.v0[j] = clamp(this.vel[j], -lim, lim) * 0.7; }
      this.hasV0 = !cold;
    }
    for (let i = 0; i < tr.t.length; i++) {
      if (i === 0 && tr.fixedStart && !cold) continue; // drawing again from the hand, not the hip
      this.kp.set(tr.p.subarray(i * N, i * N + N), n * N);
      this.kt[n] = Math.max(tr.t[i], n ? this.kt[n - 1] + 0.02 : 0);
      n++;
    }
    this.nk = n;
  }

  /** the resting pose for this frame: guard, or the run carry at speed, with a breath and the run bob */
  private rest(host: BladeHost, time: number, out: Float32Array) {
    const g = this.def.guard, r = this.def.run;
    const k = smooth(clamp((host.runAmt - 0.45) / 0.4, 0, 1)) * (0.5 + 0.5 * host.sprintW);
    for (let j = 0; j < N; j++) out[j] = g[j] + ((IS_ANG[j] ? near(r[j], g[j]) : r[j]) - g[j]) * k;
    const bob = Math.sin(host.runPhase * TAU * 2) * host.runAmt;
    out[H] += 0.02 * Math.sin(time * 1.7) + 0.03 * bob; out[LH] += 0.02 * Math.sin(time * 1.7 + 0.6) - 0.03 * bob;
    out[AZ] += 0.04 * Math.sin(time * 1.1);
  }

  /** Pose the arms and place the blade. Called at the end of Cowboy.update, after the gun is stowed. */
  update(host: BladeHost, c: CowboyRS, dt: number, time: number, alive: boolean, cloak: number, visible: boolean, camPos?: THREE.Vector3) {
    const def = this.def, B = this.B, Bn = host.bones;
    const age = c.meleeAge;
    const drawn = alive && visible && (!!c.blade || age < 0.12) && c.move !== 3 && c.skyT < 0.05;
    // a new swing: the age reset (local prediction or the Ev.Melee for others)
    const stp = clamp(c.bladeStep ?? 0, 0, 3);
    if (alive && age < 0.3 && (age < this.prevAge - 1e-4 || stp !== this.lastStep)) this.begin(stp, !this.wasDrawn || this.drawnW < 0.3);
    this.prevAge = age; this.lastStep = stp;
    this.wasDrawn = drawn;
    this.swingT = this.step >= 0 ? this.stopClock(age) : 9; // the swing clock is the cowboy's melee age (same for every viewer)
    // the arms come in fast on the draw and let go a bit slower; the blade lights over the first beat of the draw
    this.drawnW = drawn ? Math.min(1, this.drawnW + dt / 0.06) : Math.max(0, this.drawnW - dt / 0.14);
    const lit = drawn && (this.step !== 0 || this.swingT > 0.02) && host.gunStowed > 0.6;
    this.ign = lit ? Math.min(1, this.ign + dt / 0.11) : Math.max(0, this.ign - dt / 0.1);
    const w = smooth(this.drawnW);

    // ---- this frame's pose: the flourish, then a settle into the guard / run carry
    const tr = this.step >= 0 ? def.steps[this.step] : null;
    this.rest(host, time, _p1);
    if (tr && this.swingT < this.kt[this.nk - 1] + 0.3) {
      this.evalAt(this.swingT, _p0);
      const end = this.kt[this.nk - 1];
      const u = clamp((this.swingT - end) / 0.32, 0, 1);
      const s = 1 + 2.4 * Math.pow(u - 1, 3) + 1.4 * Math.pow(u - 1, 2); // ease-out-back: overshoots ~8% and settles
      if (s > 0) for (let j = 0; j < N; j++) { const g = IS_ANG[j] ? near(_p1[j], _p0[j]) : _p1[j]; _p0[j] += (g - _p0[j]) * s; }
    } else {
      if (tr) this.step = -1;
      _p0.set(_p1);
      for (const j of ANG) _p0[j] = near(_p0[j], this.cur[j]);
    }
    // the body leads the blade: hips and chest turn, lean, step and sink a beat before the hand arrives
    if (tr && this.swingT < this.kt[this.nk - 1]) {
      this.evalAt(this.swingT + BODY_LEAD, _p2);
      const k = 1 - smooth(clamp((this.swingT - this.kt[this.nk - 1] + 0.12) / 0.12, 0, 1));
      for (const j of BODY) _p0[j] += (_p2[j] - _p0[j]) * k;
    }
    this.cur.set(_p0);
    if (dt > 1e-4) for (let j = 0; j < N; j++) {
      const d = IS_ANG[j] ? _p0[j] - near(this.prevCur[j], _p0[j]) : _p0[j] - this.prevCur[j];
      this.vel[j] += (d / dt - this.vel[j]) * Math.min(1, dt * 30);
    }
    this.prevCur.set(_p0);
    const P = this.cur;

    // ---- the parked hilt(s) when the blade is away
    if (w < 0.001) {
      for (let i = 0; i < this.asm.length; i++) this.park(i, host, time);
      for (const s of this.asm) s.trail.visible = false;
      this.updateRing(dt, null);
      return;
    }

    // ---- body: twist, lean and the spin (the spin turns the whole cowboy about his feet)
    const bw = 1 - this.bodyW * 0.5; // the mocap spine carries half the twist and lean when it plays
    // follow-through: twist and lean ride an underdamped spring, so the body carries past each cut and settles back
    // instead of stopping dead on the key (it starts on the pose, so a draw from rest doesn't wobble)
    const F = this.ft;
    if (!this.ftInit || w < 0.01) { F[0] = P[TW]; F[1] = 0; F[2] = P[LN]; F[3] = 0; this.ftInit = true; }
    else if (dt > 1e-4) {
      const om = TAU * FT_HZ, h = Math.min(dt, 1 / 30);
      F[1] += (om * om * (P[TW] - F[0]) - 2 * FT_ZETA * om * F[1]) * h; F[0] += F[1] * h;
      F[3] += (om * om * (P[LN] - F[2]) - 2 * FT_ZETA * om * F[3]) * h; F[2] += F[3] * h;
    }
    const tw = F[0] * w * bw, ln = F[2] * w * bw, sp = P[SP] * w;
    if (Math.abs(sp) > 1e-4) { _q.setFromAxisAngle(Y_AXIS, -sp); host.group.quaternion.multiply(_q); }
    // the step-in and the crouch under the strike: the lead foot goes out, the back leg drives, the hips sink.
    // Off on the run (the legs are running), and only ever a visual lean: the sim position never moves.
    // Under a mocap body the clip's own legs step; the authored lunge and crouch still ride on top at part weight
    // (the crouch loads and the slam's plant are ours, the clips don't have them).
    const legW0 = w * (1 - smooth(clamp(host.runAmt / 0.5, 0, 1)));
    const legW = legW0 * (1 - this.bodyW);
    const fwT = P[FW] * legW0 * (1 - 0.5 * this.bodyW), fw = P[FW] * legW, cr = Math.max(0, P[CR]) * legW0 * (1 - 0.35 * this.bodyW);
    const fy = c.yaw;
    if (fwT > 1e-3) { host.group.position.x += -Math.sin(fy) * fwT * 0.32; host.group.position.z += -Math.cos(fy) * fwT * 0.32; }
    if (fw > 1e-3) {
      if (B.LeftUpLeg >= 0) host.rotBone(B.LeftUpLeg, X_AXIS, -0.55 * fw);
      if (B.LeftLeg >= 0) host.rotBone(B.LeftLeg, X_AXIS, 0.45 * fw);
      if (B.RightUpLeg >= 0) host.rotBone(B.RightUpLeg, X_AXIS, 0.3 * fw);
      if (B.RightLeg >= 0) host.rotBone(B.RightLeg, X_AXIS, 0.25 * fw);
    }
    // hip drive: the hips turn into the cut first and the chest finishes it (the legs counter so the feet stay put)
    const hd = -tw * 0.3;
    host.rotBone(B.Hips, Y_AXIS, hd);
    if (B.LeftUpLeg >= 0) host.rotBone(B.LeftUpLeg, Y_AXIS, -hd);
    if (B.RightUpLeg >= 0) host.rotBone(B.RightUpLeg, Y_AXIS, -hd);
    // the crouch: the hips sink and the legs fold by IK back onto wherever the feet already were, so it works the
    // same over a mocap stance as over the authored one and never lifts or sinks a foot
    const drop = cr + 0.05 * fw;
    if (drop > 1e-3 && B.LeftFoot >= 0 && B.RightFoot >= 0) {
      host.group.updateMatrixWorld(true);
      _fwd.set(-Math.sin(fy), 0, -Math.cos(fy));
      for (let k = 0; k < 2; k++) {
        const ft = Bn[k ? B.RightFoot : B.LeftFoot];
        ft.getWorldPosition(_footP[k]); ft.getWorldQuaternion(_footQ[k]);
        Bn[k ? B.RightLeg : B.LeftLeg].getWorldPosition(_kneeP[k]).addScaledVector(_fwd, 0.4);
      }
      Bn[B.Hips].position.y -= drop * this.hipUnit;
      Bn[B.Hips].updateMatrixWorld(true);
      for (let k = 0; k < 2; k++) {
        ik2(Bn[k ? B.RightUpLeg : B.LeftUpLeg], Bn[k ? B.RightLeg : B.LeftLeg], Bn[k ? B.RightFoot : B.LeftFoot], _footP[k], _kneeP[k]);
        setWorldQ(Bn[k ? B.RightFoot : B.LeftFoot], _footQ[k], 1);
      }
    }
    // the pivot step: on a spin the feet take turns, one leg lifting through each half turn (no mocap on the spins)
    const spV = Math.abs(this.vel[SP]) * legW;
    if (spV > 0.5) {
      const half = Math.abs(P[SP]) / Math.PI, lift = smooth(clamp((spV - 0.5) / 8, 0, 1)) * Math.sin(Math.PI * (half % 1));
      const up = (half | 0) % 2 === 0 ? B.LeftUpLeg : B.RightUpLeg, lo = up === B.LeftUpLeg ? B.LeftLeg : B.RightLeg;
      if (up >= 0) host.rotBone(up, X_AXIS, -0.35 * lift);
      if (lo >= 0) host.rotBone(lo, X_AXIS, 0.6 * lift);
    }
    host.rotBone(B.Spine, Y_AXIS, -tw * 0.15); host.rotBone(B.Spine1, Y_AXIS, -tw * 0.25); host.rotBone(B.Spine2, Y_AXIS, -tw * 0.3);
    host.rotBone(B.Spine, X_AXIS, ln * 0.4); host.rotBone(B.Spine1, X_AXIS, ln * 0.35); host.rotBone(B.Spine2, X_AXIS, ln * 0.25);
    host.group.updateMatrixWorld(true);

    // ---- the aim frame (the spin turns it with the body)
    const yaw = c.yaw - sp;
    _fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw)); _rgt.set(Math.cos(yaw), 0, -Math.sin(yaw));
    Bn[B.Spine2].getWorldPosition(_cur);
    const sY = _cur.y + this.shoulderOff;

    // ---- right hand: blade frame from the pose, the hand placed so the fist closes on the hilt
    this.handTarget(P, AZ, _cur, sY, _hilt);
    dirOf(P[YAW], P[PIT], _fwd, _rgt, _D).normalize();
    const s0 = this.asm[0];
    this.edgeFor(s0, P, AZ, _D, dt, time);
    this.bladeQ(_D, s0.edge, _qb);
    this.gripQ(_qb, def.reverse, _q2);            // the gun frame that puts the blade in the fist
    _q.copy(this.a.grip.q).invert().premultiply(_q2); // hand world rotation = gun * inv(grip)
    _v.copy(this.a.grip.pos).applyQuaternion(_q);
    _v2.copy(_hilt).sub(_v);                       // the wrist target
    Bn[B.RightArm].getWorldPosition(_sh);
    clampReach(_sh, _v2, this.armL);
    _pole.copy(_sh).addScaledVector(_rgt, 0.4).addScaledVector(UP, -0.5).addScaledVector(_fwd, -0.25);
    Bn[B.RightHand].getWorldPosition(_v3);
    _v3.lerp(_v2, w);
    ik2(Bn[B.RightArm], Bn[B.RightForeArm], Bn[B.RightHand], _v3, _pole);
    setWorldQ(Bn[B.RightHand], _q, w);
    // the blade rides the real hand from here (it never floats off the fist)
    Bn[B.RightHand].getWorldQuaternion(_q2);
    _hilt.copy(this.a.grip.pos).applyQuaternion(_q2).add(Bn[B.RightHand].getWorldPosition(_v));
    _q2.multiply(this.a.grip.q);                   // gun frame
    this.bladeFromGrip(_q2, def.reverse, _qb);
    const vis = cloak > 0.02 ? 1 - 0.75 * cloak : 1;
    // a minimum on-screen width: the energy fattens as the camera pulls back (about 6 px at play distance)
    const dist = camPos ? camPos.distanceTo(_hilt) : 3;
    const thick = 1.5 * clamp(Math.pow(dist / 3, 0.75), 1, 2.6);
    const slamFlash = this.ringAge < 0.2 ? 1 - this.ringAge / 0.2 : 0;
    s0.place(_hilt, _qb, smooth(Math.min(1, this.ign * 1.4)), this.ign, vis, def, time, thick, slamFlash);
    this.trailFor(0, s0, P, _cur, sY, yaw, c.yaw, _hilt, vis);

    // ---- left hand: the shaft (two-handers), the second dagger (Phantom) or the free hand
    if (def.twoHand > 0) {
      // the off hand stays locked on the shaft: its grip point, or the nearest point along the shaft it can reach
      _D.set(0, 1, 0).applyQuaternion(_qb);
      Bn[B.LeftArm].getWorldPosition(_v3);
      _v2.copy(_hilt).addScaledVector(_D, -def.twoHand);
      if (_v2.distanceTo(_v3) > this.armLL * 0.95) _v2.copy(_hilt).addScaledVector(_D, clamp(_v3.sub(_hilt).dot(_D), -def.twoHand - 0.15, -0.18));
      this.leftTo(host, _v2, _sh, w, _fwd);
    } else if (def.dual) {
      const s1 = this.asm[1];
      this.handTarget(P, LAZ, _cur, sY, _v2);
      this.leftTo(host, _v2, _sh, w, _fwd);
      // the second dagger sits in the left palm on its own authored line
      this.palm(Bn, B.LeftHand, B.LeftHandMiddle1, _hilt);
      dirOf(P[LYAW], P[LPIT], _fwd, _rgt, _D).normalize();
      this.edgeFor(s1, P, LAZ, _D, dt, time);
      this.bladeQ(_D, s1.edge, _qb);
      s1.place(_hilt, _qb, 1, this.ign, vis, def, time, thick);
      this.trailFor(1, s1, P, _cur, sY, yaw, c.yaw, _hilt, vis);
    } else {
      this.handTarget(P, LAZ, _cur, sY, _v2);
      this.leftTo(host, _v2, _sh, w, _fwd);
    }
    // the slam's shockwave
    this.updateRing(dt, tr && tr.slamAt !== undefined && !this.slammed && this.swingT >= tr.slamAt ? s0 : null);
    if (tr && tr.slamAt !== undefined && this.swingT >= tr.slamAt) this.slammed = true;
    this.ringY = c.y;
  }
  private ringY = 0;

  private park(i: number, host: BladeHost, time: number) {
    const s = this.asm[i];
    _m.multiplyMatrices(host.bones[this.B.Hips].matrixWorld, this.hipLocal[i]);
    _m.decompose(_v, _q, _v2);
    s.place(_v, _q, 0, 0, 1, this.def, time);
  }

  /** world point for a hand from its (azimuth, height, reach) pose fields */
  private handTarget(P: Float32Array, i0: number, chest: THREE.Vector3, shoulderY: number, out: THREE.Vector3) {
    const az = P[i0], d = P[i0 + 2] * this.kA;
    return out.copy(chest).addScaledVector(_fwd, Math.cos(az) * d).addScaledVector(_rgt, Math.sin(az) * d).setY(shoulderY + (P[i0 + 1] - 1.5) * this.kH);
  }

  /** the cutting edge leads the swing: the tip's velocity across the blade, falling back to "forward" at rest */
  private edgeFor(s: Assembly, P: Float32Array, i0: number, D: THREE.Vector3, dt: number, time: number) {
    void P; void i0; void time;
    _E.copy(_fwd).addScaledVector(D, -_fwd.dot(D));
    if (_E.lengthSq() < 0.04) _E.copy(UP).addScaledVector(D, -UP.dot(D));
    _E.normalize();
    // velocity of the blade direction from the curve (a finite difference of this flourish), when swinging
    if (this.step >= 0 && this.swingT < this.kt[this.nk - 1]) {
      this.evalAt(this.swingT + 0.012, _p2);
      const yi = i0 === AZ ? YAW : LYAW;
      dirOf(_p2[yi], _p2[yi + 1], _fwd, _rgt, _X).normalize().sub(D);
      _X.addScaledVector(D, -_X.dot(D));
      const sp = _X.length() / 0.012; // rad/s
      if (sp > 1) _E.lerp(_X.normalize(), clamp((sp - 1) / 6, 0, 1)).normalize();
    }
    if (s.edgeFresh) { s.edge.copy(_E); s.edgeFresh = false; }
    else s.edge.lerp(_E, 1 - Math.exp(-28 * dt));
    s.edge.addScaledVector(D, -s.edge.dot(D));
    if (s.edge.lengthSq() < 1e-6) s.edge.copy(_E);
    s.edge.normalize();
  }

  /** blade frame: X = D x E, Y = D, Z = E */
  private bladeQ(D: THREE.Vector3, E: THREE.Vector3, out: THREE.Quaternion) {
    _X.crossVectors(D, E);
    _m2.makeBasis(_X, D, E);
    return out.setFromRotationMatrix(_m2);
  }
  /** gun frame (+Z barrel, +Y top) whose fist would hold this blade: the blade leaves the thumb side of the fist
   *  (gun up, tilted forward with the grip's rake), or the little-finger side for a reverse grip */
  private gripQ(bladeQ: THREE.Quaternion, reverse: boolean, out: THREE.Quaternion) {
    return out.copy(bladeQ).multiply(reverse ? GUN_FROM_BLADE_REV : GUN_FROM_BLADE);
  }
  private bladeFromGrip(gunQ: THREE.Quaternion, reverse: boolean, out: THREE.Quaternion) {
    return out.copy(gunQ).multiply(reverse ? BLADE_FROM_GUN_REV : BLADE_FROM_GUN);
  }

  private leftTo(host: BladeHost, palmTarget: THREE.Vector3, _rs: THREE.Vector3, w: number, fwd: THREE.Vector3) {
    const B = this.B, Bn = host.bones;
    // aim the palm, not the wrist, at the target
    this.palm(Bn, B.LeftHand, B.LeftHandMiddle1, _v3);
    Bn[B.LeftHand].getWorldPosition(_v);
    _v2.copy(palmTarget).sub(_v3.sub(_v)); // wrist target
    Bn[B.LeftArm].getWorldPosition(_sh);
    clampReach(_sh, _v2, this.armLL);
    _pole.copy(_sh).addScaledVector(_rgt, -0.4).addScaledVector(UP, -0.5).addScaledVector(fwd, -0.25);
    _v.lerp(_v2, w);
    ik2(Bn[B.LeftArm], Bn[B.LeftForeArm], Bn[B.LeftHand], _v, _pole);
  }

  private palm(Bn: THREE.Bone[], hand: number, mid: number, out: THREE.Vector3) {
    Bn[hand].getWorldPosition(out);
    if (mid >= 0) { Bn[mid].getWorldPosition(_off); out.lerp(_off, 0.55); }
    return out;
  }

  /** the swept arc: this flourish's curve sampled back over the last ~0.25 s, shifted onto the real hilt */
  private trailFor(i: number, s: Assembly, P: Float32Array, chest: THREE.Vector3, sY: number, yawNow: number, aimYaw: number, hiltNow: THREE.Vector3, vis: number) {
    void P; void yawNow;
    const def = this.def;
    const swinging = this.step >= 0 && this.swingT < this.kt[this.nk - 1] + 0.12 && this.ign > 0.5;
    s.trail.visible = swinging;
    if (!swinging) return;
    const pos = s.tPos, at = s.tAttr;
    const ai = i === 0 ? AZ : LAZ, yi = i === 0 ? YAW : LYAW;
    const inner = def.tr0, outer = def.tr1;
    const cutEnd = this.step === 3 ? 0.38 : def.slab ? 0.34 : 0.22;
    const tdt = def.slab ? TRAIL_DT * 1.7 : TRAIL_DT; // the maul's heavy arc hangs in the air longer
    let lx = 0, ly = 0, lz = 0, px = 0, py = 0, pz = 0, broken = false;
    for (let k = 0; k < TRAIL_N; k++) {
      const t = Math.max(0, this.swingT - k * tdt);
      this.evalAt(t, _p2);
      const yw = aimYaw - _p2[SP];
      _fwd.set(-Math.sin(yw), 0, -Math.cos(yw)); _rgt.set(Math.cos(yw), 0, -Math.sin(yw));
      this.handTarget(_p2, ai, chest, sY, _v);
      dirOf(_p2[yi], _p2[yi + 1], _fwd, _rgt, _D).normalize();
      if (k === 0) _off.copy(hiltNow).sub(_v);
      _v.add(_off);
      const o = k * 6;
      // a crescent, not a sheet: a band along the outer part of the blade that tapers to nothing at the tail
      const inK = outer - (outer - inner) * 0.34 * (1 - k / (TRAIL_N - 1));
      pos[o] = _v.x + _D.x * inK; pos[o + 1] = _v.y + _D.y * inK; pos[o + 2] = _v.z + _D.z * inK;
      const tx = _v.x + _D.x * outer, ty = _v.y + _D.y * outer, tz = _v.z + _D.z * outer;
      pos[o + 3] = tx; pos[o + 4] = ty; pos[o + 5] = tz;
      // fade with age, and with how fast the tip was moving there (a slow blade leaves no streak)
      const dx = tx - lx, dy = ty - ly, dz = tz - lz;
      const seg = k ? Math.hypot(dx, dy, dz) / tdt : 0;
      // where the tip turned back on itself (a reversal, not a curve) the ribbon ends: older samples fade out
      // instead of folding into a V
      if (k > 1 && dx * px + dy * py + dz * pz < 0 && seg > 1.5) broken = true;
      if (k) { px = dx; py = dy; pz = dz; }
      lx = tx; ly = ty; lz = tz;
      const age = Math.pow(1 - k / (TRAIL_N - 1), 2.0);
      // the cut burns bright; the wrist flourish after it leaves only a wisp (finishers stay bright to the end)
      const wisp = 1 - 0.35 * smooth(clamp((t - cutEnd) / 0.07, 0, 1));
      const a = broken ? 0 : age * wisp * clamp((seg - (def.slab ? 0.8 : 1.5)) / 4.5, 0, 1) * (t > 0 ? 1 : 0);
      at[k * 4] = a; at[k * 4 + 1] = 0; at[k * 4 + 2] = a; at[k * 4 + 3] = 1;
    }
    at[0] = at[2] = at[4]; // the head sample has no segment of its own
    // restore the live frame for the caller
    const yaw = aimYaw - this.cur[SP] * smooth(this.drawnW);
    _fwd.set(-Math.sin(yaw), 0, -Math.cos(yaw)); _rgt.set(Math.cos(yaw), 0, -Math.sin(yaw));
    s.mTrail.uniforms.uInt.value = 2.2 * s.trailGain * vis * this.ign;
    (s.trail.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    (s.trail.geometry.attributes.aT as THREE.BufferAttribute).needsUpdate = true;
  }

  private updateRing(dt: number, fire: Assembly | null) {
    const r = this.ring;
    if (!r) return;
    if (fire) {
      // the head hits the ground: the ring starts under it
      _v.set(0, 1.0, 0).applyMatrix4(fire.root.matrixWorld);
      r.position.set(_v.x, this.ringY + 0.06, _v.z);
      this.ringAge = 0;
    }
    this.ringAge += dt;
    const t = this.ringAge / 0.3;
    r.visible = t < 1;
    if (!r.visible) return;
    const s = 0.5 + 2.6 * (1 - Math.pow(1 - t, 3));
    r.scale.set(s, 1, s);
    (r.material as THREE.ShaderMaterial).uniforms.uA.value = (1 - t) * (1 - t);
  }
}

// gun frame <-> blade frame. In the gun frame the blade leaves the fist along (0, cos a, sin a) (up, raked forward by
// the grip angle a) with its edge along (0, -sin a, cos a) (the knuckles); reverse grip flips the blade.
const RAKE = 0.28;
const GUN_FROM_BLADE = (() => {
  const d = new THREE.Vector3(0, Math.cos(RAKE), Math.sin(RAKE)), e = new THREE.Vector3(0, -Math.sin(RAKE), Math.cos(RAKE));
  const x = new THREE.Vector3().crossVectors(d, e);
  // blade frame (x, d, e) expressed in gun space; we need gun-from-blade's inverse: Q_gun = Q_blade * inv(M)
  return new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, d, e)).invert();
})();
const GUN_FROM_BLADE_REV = (() => {
  const d = new THREE.Vector3(0, -Math.cos(RAKE), -Math.sin(RAKE)), e = new THREE.Vector3(0, -Math.sin(RAKE), Math.cos(RAKE));
  const x = new THREE.Vector3().crossVectors(d, e);
  return new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, d, e)).invert();
})();
const BLADE_FROM_GUN = GUN_FROM_BLADE.clone().invert();
const BLADE_FROM_GUN_REV = GUN_FROM_BLADE_REV.clone().invert();

function clampReach(shoulder: THREE.Vector3, target: THREE.Vector3, len: number) {
  _X.copy(target).sub(shoulder);
  const d = _X.length(), m = len * 0.97;
  if (d > m) target.copy(shoulder).addScaledVector(_X, m / d);
}

// two-bone IK (the same solve as index.ts ADS / foregrip; kept local so the lanes don't share a private helper)
const _ia = new THREE.Vector3(), _ib = new THREE.Vector3(), _ic = new THREE.Vector3(), _ie = new THREE.Vector3(), _id = new THREE.Vector3(), _in = new THREE.Vector3();
const _iq = new THREE.Quaternion(), _iw = new THREE.Quaternion(), _ip = new THREE.Quaternion();
function worldRotate(bone: THREE.Object3D, rot: THREE.Quaternion) {
  bone.getWorldQuaternion(_iw);
  _iw.premultiply(rot);
  (bone.parent as THREE.Object3D).getWorldQuaternion(_ip);
  bone.quaternion.copy(_ip.invert().multiply(_iw));
  bone.updateMatrixWorld(true);
}
function ik2(upper: THREE.Bone, lower: THREE.Bone, end: THREE.Bone, target: THREE.Vector3, pole: THREE.Vector3) {
  upper.getWorldPosition(_ia); lower.getWorldPosition(_ib); end.getWorldPosition(_ic);
  const l1 = _ia.distanceTo(_ib), l2 = _ib.distanceTo(_ic);
  _id.subVectors(target, _ia);
  const d = clamp(_id.length(), 0.05, (l1 + l2) * 0.999);
  _id.normalize();
  const cosA = clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1);
  _in.subVectors(pole, _ia); _in.addScaledVector(_id, -_in.dot(_id));
  if (_in.lengthSq() < 1e-8) _in.set(0, -1, 0);
  _in.normalize();
  _ie.copy(_ia).addScaledVector(_id, cosA * l1).addScaledVector(_in, Math.sqrt(1 - cosA * cosA) * l1);
  _iq.setFromUnitVectors(_ib.sub(_ia).normalize(), _ie.sub(_ia).normalize());
  worldRotate(upper, _iq);
  lower.getWorldPosition(_ib); end.getWorldPosition(_ic);
  _iq.setFromUnitVectors(_ic.sub(_ib).normalize(), _ie.copy(target).sub(_ib).normalize());
  worldRotate(lower, _iq);
}
/** set a bone's world rotation (blended by w from where it is), keeping its children attached */
function setWorldQ(bone: THREE.Bone, q: THREE.Quaternion, w: number) {
  (bone.parent as THREE.Object3D).getWorldQuaternion(_ip);
  _iw.copy(_ip).invert().multiply(q);
  bone.quaternion.slerp(_iw, w);
  bone.updateMatrixWorld(true);
}
